import { useMemo } from "react";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, BarChart, Bar, Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartConfig } from "@/components/ui/chart";
import type { PortfolioProject, PortfolioROISummary } from "@/hooks/usePortfolioProjects";
import { calculateROI } from "@/data/roiModelData";

// ── Helpers ──
function getClassification(score: number) {
  if (score >= 400) return "Quick Win";
  if (score >= 300) return "Strategic Play";
  if (score >= 200) return "Experiment";
  return "Back-log";
}

const CLASSIFICATION_COLORS: Record<string, string> = {
  "Quick Win": "hsl(var(--success))",
  "Strategic Play": "hsl(var(--info))",
  Experiment: "hsl(var(--warning))",
  "Back-log": "hsl(var(--muted-foreground))",
};

// ── Scatter: Score vs ROI ──
export function PortfolioScatterChart({ projects }: { projects: PortfolioProject[] }) {
  const data = useMemo(() =>
    projects.map(p => {
      let roi = 0;
      if (p.roiData && p.roiData.costs && p.roiData.benefits && Array.isArray(p.roiData.benefits)) {
        const benefits = p.roiData.benefits.reduce((s, b) => s + (b.type === "not_defined" ? 0 : b.amount), 0);
        const costs = (p.roiData.costs.subscriptions || 0) + (p.roiData.costs.tokenConsumption || 0) + (p.roiData.costs.personnel || 0) + (p.roiData.costs.training || 0);
        roi = calculateROI(benefits, costs);
      }
      return {
        name: p.name,
        company: p.company || "Sin empresa",
        score: p.totalScore,
        roi: Math.round(roi),
        classification: getClassification(p.totalScore),
      };
    }),
  [projects]);

  const chartConfig: ChartConfig = {
    score: { label: "Score LIDER-IA" },
    roi: { label: "ROI %" },
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle>Matriz Score vs ROI</CardTitle>
        <CardDescription>Cada punto es un proyecto. Color según clasificación.</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[350px] w-full">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              type="number" dataKey="score" name="Score"
              domain={[0, 500]} tickCount={6}
              label={{ value: "Score LIDER-IA", position: "bottom", offset: 0, fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis
              type="number" dataKey="roi" name="ROI %"
              label={{ value: "ROI %", angle: -90, position: "insideLeft", fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              stroke="hsl(var(--muted-foreground))"
            />
            <ZAxis range={[80, 80]} />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
                    <p className="font-semibold text-foreground">{d.name}</p>
                    <p className="text-muted-foreground">{d.company}</p>
                    <p className="mt-1">Score: <strong>{d.score}</strong></p>
                    <p>ROI: <strong>{d.roi}%</strong></p>
                    <p className="mt-1 font-medium" style={{ color: CLASSIFICATION_COLORS[d.classification] }}>
                      {d.classification}
                    </p>
                  </div>
                );
              }}
            />
            <Scatter data={data} isAnimationActive={false}>
              {data.map((entry, i) => (
                <Cell key={i} fill={CLASSIFICATION_COLORS[entry.classification]} fillOpacity={0.8} />
              ))}
            </Scatter>
          </ScatterChart>
        </ChartContainer>
        <div className="flex justify-center gap-6 mt-2">
          {Object.entries(CLASSIFICATION_COLORS).map(([label, color]) => (
            <div key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
              {label}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Pie: Distribution by classification ──
export function ClassificationPieChart({ projects }: { projects: PortfolioProject[] }) {
  const data = useMemo(() => {
    const counts: Record<string, number> = { "Quick Win": 0, "Strategic Play": 0, Experiment: 0, "Back-log": 0 };
    projects.forEach(p => counts[getClassification(p.totalScore)]++);
    return Object.entries(counts).map(([name, value]) => ({ name, value, fill: CLASSIFICATION_COLORS[name] }));
  }, [projects]);

  const chartConfig: ChartConfig = {
    value: { label: "Proyectos" },
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle>Distribución por Clasificación</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[280px] w-full">
          <PieChart>
            <Pie
              data={data} dataKey="value" nameKey="name"
              cx="50%" cy="50%" outerRadius={90} innerRadius={50}
              label={({ name, value }) => value > 0 ? `${name}: ${value}` : ""}
              labelLine={false}
              isAnimationActive={false}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
                    <p className="font-semibold">{d.name}</p>
                    <p>{d.value} proyectos</p>
                  </div>
                );
              }}
            />
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

// ── Bar: Top 5 by ROI ──
export function TopROIBarChart({ roiSummaries }: { roiSummaries: PortfolioROISummary[] }) {
  const data = useMemo(
    () =>
      [...roiSummaries]
        .sort((a, b) => b.roi - a.roi)
        .slice(0, 7)
        .map(p => ({
          name: p.projectName.length > 25 ? p.projectName.slice(0, 25) + "…" : p.projectName,
          roi: Math.round(p.roi),
          company: p.company,
        })),
    [roiSummaries],
  );

  const chartConfig: ChartConfig = {
    roi: { label: "ROI %", color: "hsl(var(--success))" },
  };

  if (data.length === 0) return null;

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle>Top Proyectos por ROI</CardTitle>
        <CardDescription>Los {data.length} proyectos con mayor retorno</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
            <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
            <YAxis
              dataKey="name" type="category" width={160}
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
                    <p className="font-semibold">{d.name}</p>
                    <p className="text-muted-foreground">{d.company}</p>
                    <p className="mt-1 text-success font-bold">ROI: {d.roi}%</p>
                  </div>
                );
              }}
            />
            <Bar dataKey="roi" fill="hsl(var(--success))" radius={[0, 6, 6, 0]} barSize={20} isAnimationActive={false} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
